import {objectFromEntries} from '@benchling/js-utils';
import {produce} from 'immer';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcedureStepGroup,
  ProcedureStepConfig,
  ProcedureStepGroupType,
  ProcedureFlowchart,
  Method,
  MethodNode,
} from 'procedures/ProcedureDomainTypes';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  PersistenceInfo,
} from 'procedures/ProcedurePageContext';
import {
  getProcedurePlatformFeatureConfig,
  ProcedurePlatformFeature,
} from 'procedures/procedures-platform/FeatureOptions';
import {isMethodNode} from 'procedures/processes/MethodNodeUtils';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import removeNulls from 'util/removeNulls';

import {duplicateStep, normalizeNameForUniqueness} from '../flowchartManagementUtils';
import {STEP_NAME_PLACEHOLDER} from './StepConfigConstants';

const STEP_GROUP_TYPE_TO_PROCEDURE_PLATFORM_FEATURE_MAP: {
  [stepGroupType in ProcedureStepGroupType]: ProcedurePlatformFeature;
} = {
  [ProcedureStepGroupType.PRE_RUN]: ProcedurePlatformFeature.HAS_PRE_RUN_STEP,
  [ProcedureStepGroupType.RUN]: ProcedurePlatformFeature.HAS_RUN_STEP,
  [ProcedureStepGroupType.POST_RUN]: ProcedurePlatformFeature.HAS_POST_RUN_STEP,
};

const ORDERED_DEFAULT_STEP_GROUP_TYPES = [
  ProcedureStepGroupType.PRE_RUN,
  ProcedureStepGroupType.RUN,
  ProcedureStepGroupType.POST_RUN,
];

export function getOrderedStepGroupTypes(procedureType: ProcedureType): Array<ProcedureStepGroupType> {
  return ORDERED_DEFAULT_STEP_GROUP_TYPES.filter((stepGroupType) =>
    getProcedurePlatformFeatureConfig(
      STEP_GROUP_TYPE_TO_PROCEDURE_PLATFORM_FEATURE_MAP[stepGroupType],
      procedureType
    )
  );
}

export function makeEmptyStepGroups(procedureType: ProcedureType): Array<ProcedureStepGroup> {
  const orderedStepGroupTypes = getOrderedStepGroupTypes(procedureType);
  return orderedStepGroupTypes.map((group) => ({group, steps: []}));
}

export function findStep(stepGroups: Array<ProcedureStepGroup>, stepCid: string): ProcedureStepConfig | null {
  return findStepWithGroup(stepGroups, stepCid).step;
}

export function findStepWithGroup(
  stepGroups: Array<ProcedureStepGroup>,
  stepCid: string
): {step: ProcedureStepConfig; group: ProcedureStepGroupType} | {step: null; group: null} {
  for (const stepGroup of stepGroups) {
    const step = stepGroup.steps.find((s) => s.cid === stepCid);
    if (step != null) {
      return {step, group: stepGroup.group};
    }
  }
  return {step: null, group: null};
}

export function makeEmptyStep(name?: string, fromId: string | null = null): ProcedureStepConfig {
  return {
    id: null,
    cid: uuidv4(),
    name: name ?? '',
    documentId: null,
    documentVersionUpdate: null,
    fromId,
  };
}

export function getFirstStepCidForMethod(method: Method): string | null {
  const steps = collectAllSteps(method.stepGroups);
  return steps[0]?.cid ?? null;
}

export function getDefaultStepCidForPoWMethodCidMap(
  nodes: Record<string, TaskDefinitionVersionSpec<unknown>>
): {
  [methodCid: string]: string | null;
} {
  const methodNodesOnly: Record<string, TaskDefinitionVersionSpec<MethodNode>> = {};
  for (const [nodeId, node] of Object.entries(nodes)) {
    if (isMethodNode(node)) {
      methodNodesOnly[nodeId] = node;
    }
  }

  return objectFromEntries(
    Object.entries(methodNodesOnly).map(([_nodeId, node]) => {
      const steps = collectAllSteps(node.newProcessTaskDefinitionVersion.applicationData?.stepGroups ?? []);
      return [node.clientId, steps.length > 0 ? steps[0].cid : null];
    })
  );
}

/**
 * Return first available step after the deleted step document if there is one in the step group. Otherwise
 * use the first document step for the method if any exist
 * @param steps
 * @param stepGroups
 * @param stepIndex
 */
export function getNextStepCidAfterRemove(
  steps: Array<ProcedureStepConfig>,
  stepGroups: Array<ProcedureStepGroup>,
  stepIndex: number
): string {
  const newStep =
    steps.length > 0
      ? (steps[stepIndex] ?? steps[steps.length - 1])
      : stepGroups.flatMap((stepGroup) => stepGroup.steps)[0];
  return newStep?.cid;
}

function collectAllSteps(stepGroups: Array<ProcedureStepGroup>): Array<ProcedureStepConfig> {
  return stepGroups.flatMap((group) => group.steps);
}

export function collectAllStepNames(
  stepGroups: Array<ProcedureStepGroup>,
  exceptThisStepCid?: string
): Array<string> {
  return collectAllSteps(stepGroups)
    .filter((step) => step.cid !== exceptThisStepCid)
    .map((step) => step.name);
}

export function collectAllStepDocumentIds(stepGroups: Array<ProcedureStepGroup>): Array<string> {
  return removeNulls(collectAllSteps(stepGroups).map((step) => step.documentId));
}

/**
 * Add a new empty step to the specified step group at the specified index
 * @param fc The flowchart containing the method
 * @param methodCid The cid of the method to add a new step to
 */
export function addStepToStepGroup(
  fc: ProcedureFlowchart,
  methodCid: string,
  stepGroupIndex: number,
  stepIndex: number,
  stepName?: string,
  fromId?: string
): {updatedStepGroups: Array<ProcedureStepGroup>; newStepCid: string} {
  const originalMethod = fc.nodes.find((n) => n.cid === methodCid);
  assert(originalMethod != null, 'Expected to find method', {fc, methodCid});
  return addStepToStepGroupByMethod(originalMethod, stepGroupIndex, stepIndex, stepName, fromId);
}

/**
 * Add a new empty step directly to a method's step groups
 */
export function addStepToStepGroupByMethod(
  originalMethod: Method,
  stepGroupIndex: number,
  stepIndex: number,
  stepName?: string,
  fromId?: string
): {updatedStepGroups: Array<ProcedureStepGroup>; newStepCid: string} {
  return addStepDirectlyToStepGroup(originalMethod.stepGroups, stepGroupIndex, stepIndex, stepName, fromId);
}

export function addStepDirectlyToStepGroup(
  originalStepGroups: Array<ProcedureStepGroup>,
  stepGroupIndex: number,
  stepIndex: number,
  stepName?: string,
  fromId?: string
): {updatedStepGroups: Array<ProcedureStepGroup>; newStepCid: string} {
  assert(originalStepGroups[stepGroupIndex] != null, 'Unable to add a step; group index is incorrect');

  const stepNamesInUse = collectAllStepNames(originalStepGroups);
  const newStepName = normalizeNameForUniqueness(stepName || '', stepNamesInUse, STEP_NAME_PLACEHOLDER);
  const newStep = makeEmptyStep(newStepName, fromId);

  const updatedStepGroups = produce(originalStepGroups, (draftStepGroups) => {
    draftStepGroups[stepGroupIndex].steps.splice(stepIndex, 0, newStep);
  });

  return {
    updatedStepGroups,
    newStepCid: newStep.cid,
  };
}

/**
 * Copy the step and prepare the persistence info to return to the saveImmediately call. This
 * does not update the step groups and returns a standalone new step not associated with any step groups.
 * @param stepGroups step groups to duplicate the step from
 * @param stepGroupIndex index of the step group to duplicate the step from
 * @param stepIndex index of the step to duplicate
 * @returns standalone new step and persistence info
 */
export function copyStepWithPersistenceInfo(
  stepGroups: Array<ProcedureStepGroup>,
  stepGroupIndex: number,
  stepIndex: number
): {newStep: ProcedureStepConfig; persistenceInfo: PersistenceInfo} {
  const originalStep = stepGroups[stepGroupIndex].steps[stepIndex];
  const stepNamesInUse = collectAllStepNames(stepGroups);
  const newStepName = normalizeNameForUniqueness(originalStep.name, stepNamesInUse, STEP_NAME_PLACEHOLDER);
  const newStep = duplicateStep(originalStep, newStepName);

  assert(newStep.cid != null, 'Unexpectedly missing cid for copied step');
  assert(originalStep.fromId != null, 'Unexpectedly missing document version id for step');

  return {
    newStep,
    persistenceInfo: {
      cloneStepCidToDocumentVersionIdMap: {
        [newStep.cid]: originalStep.fromId,
      },
      blockingSaveObjectSource: BlockingSaveObjectSource.Step,
      blockingSaveOperation: BlockingSaveOperation.Duplicate,
    },
  };
}

export function getUpdatedStepGroupsWithCopiedStep(
  stepGroups: Array<ProcedureStepGroup>,
  stepGroupIndex: number,
  stepIndex: number,
  newStep: ProcedureStepConfig
): Array<ProcedureStepGroup> {
  return produce(stepGroups, (draftStepGroups) => {
    const steps = draftStepGroups[stepGroupIndex].steps;
    steps.splice(Math.min(stepIndex + 1, steps.length), 0, newStep);
  });
}
